import { countryName } from '@/ui/words';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { prospectingOf } from '@norum/contracts';
import { failureOf } from '@/api/errors';
import { useDiscover, useDraftProspect, useExcludeProspect, useImportProspects, useMarkets, useProspect, useProspects, useRetryProspect } from '@/api/hooks';
import type { WireProspect } from '@/api/wire';
import { useMeOrThrow } from '@/app/MeContext';
import { useDepartmentLabel } from '@/app/departments';
import { Badge, Button, Field, Kicker, Skeleton, TextInput } from '@/ui/primitives';
import { FactList, RecordHeader, Table, when } from '@/ui/records';
import { Refusal } from '@/ui/Refusal';
import { useToast } from '@/ui/Toast';
import { EXCLUSION_WORDS, STAGE_CHIP, STAGE_WORDS, WAY_IN_WORDS, reasonWords } from './words';
import { Textarea, Select } from '@/ui/fields';
import { useWho } from '@/ui/who';
import { explainProspect } from './explain';

/**
 * Restaurants we might write to (FR-OUT-71, FR-OUT-71b; ADR-0064).
 *
 * A person says what to look for; the department's agent that holds the
 * prospecting grant finds places, reads each one's own website, finds a
 * shared address where the country allows a first email, and leaves a draft
 * in the shared box. Every step is a job on the heartbeat, so this screen
 * refreshes rather than waits. There is no address anywhere on it — the
 * platform does not send one — and nothing Google says is kept: the link
 * opens the place on Google, live.
 */
export function Prospects() {
  const me = useMeOrThrow();
  const label = useDepartmentLabel();
  const navigate = useNavigate();
  const list = useProspects(me.department);
  const markets = useMarkets(me.department);
  const who = useWho();
  const [openId, setOpenId] = useState<string | null>(null);

  if (list.isPending) return <Skeleton rows={8} />;
  if (list.isError) return <div className="p-6"><Refusal failure={failureOf(list.error)} /></div>;

  const declared = (markets.data?.markets ?? []).filter((m) => m.declared);
  const rows = list.data.prospects;
  // The same list in Fundraising and Food Science, in their words and with
  // none of Outbound's outreach: nothing there is written to (PR-23).
  const words = prospectingOf(me.department);
  if (words && !words.outreach) {
    return (
      <div className="mx-auto flex w-full max-w-[1040px] flex-col gap-6 p-6 phone:p-4">
        <RecordHeader
          kicker={label(me.department)}
          title={words.found}
          subtitle={`What its plans found on Google and read from their own sites, scored by the profile’s rules. ${
            words.channels.includes('warm_intro') ? 'Each is reached by an introduction from someone who knows them; nothing is written to them from here.' : 'Nothing is sent from here: a person decides what to do with each.'
          }`}
        />
        <Table<WireProspect>
          title={`places ${words.found === 'Found' ? 'found' : 'we might reach'}`}
          blurb="Every point is one of the profile’s rules, checked; the score is a rubric, not a model."
          rows={rows}
          onOpen={(p) => void navigate(`/prospects/${p.id}`)}
          explain={(p) => explainProspect(p, who, { department: me.department, outreach: false })}
          testId="prospects"
          emptyTitle="Nothing found yet"
          emptyBody="A plan’s first sweep, once the superadmin has approved it, fills this list."
          columns={[
            { label: 'Name', get: (p) => p.businessName ?? p.domain ?? 'Name not written down yet', primary: true },
            { label: 'Country', get: (p) => countryName(p.marketCode) },
            { label: 'Fit', get: (p) => (p.fit ? `${p.fit.score} · ${p.fit.tier}` : '—') },
            { label: 'Step', get: (p) => STAGE_CHIP[p.stage].label, chip: (p) => STAGE_CHIP[p.stage] },
          ]}
          facets={[{ label: 'Country', get: (p) => countryName(p.marketCode) }]}
        />
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-[1040px] flex-col gap-6 p-6 phone:p-4">
      <RecordHeader
        kicker="Who we sell to"
        title="Restaurants we might write to"
        subtitle="Found on Google, read from their own websites, and — where the country allows it — given a first email that waits in the shared box for a person to send. Nothing here sends."
      />

      <Discover markets={declared.map((m) => ({ code: m.code, name: m.name }))} />

      <Table<WireProspect>
        title="restaurants"
        blurb="Scored from what each restaurant says on its own website and a few public facts, with every point explained. The score is a rubric, not a model."
        rows={rows}
        onOpen={(p) => setOpenId(p.id)}
        explain={(p) => explainProspect(p, who, { department: me.department, outreach: true })}
        testId="prospects"
        emptyTitle="No restaurants yet"
        emptyBody="Say what to look for above, or import a list you already have."
        columns={[
          { label: 'Restaurant', get: (p) => p.businessName ?? p.domain ?? 'Name not written down yet', primary: true },
          { label: 'Why now', get: (p) => p.whyNow ?? '—' },
          { label: 'Where', get: (p) => [countryName(p.marketCode), p.territory ?? p.zone].filter(Boolean).join(' · ') },
          { label: 'Fit', get: (p) => (p.fit ? `${p.fit.score} · ${p.fit.tier}` : '—') },
          { label: 'Way in', get: (p) => WAY_IN[p.wayIn ?? 'cold'].label, chip: (p) => WAY_IN[p.wayIn ?? 'cold'] },
          { label: 'Address', get: (p) => (p.hasAddress ? `${p.addressKind === 'role' ? 'Shared' : 'A person’s'} · ${p.verification ?? 'unchecked'}` : '—') },
          { label: 'Step', get: (p) => STAGE_CHIP[p.stage].label, chip: (p) => STAGE_CHIP[p.stage] },
        ]}
        facets={[
          { label: 'Stage', get: (p) => stageOf(p) },
          { label: 'Way in', get: (p) => WAY_IN[p.wayIn ?? 'cold'].label },
          { label: 'Country', get: (p) => countryName(p.marketCode) },
        ]}
      />

      {openId && <ProspectRecord id={openId} onClose={() => setOpenId(null)} />}

      <Import markets={declared.map((m) => m.code)} />
    </div>
  );
}

const WAY_IN = WAY_IN_WORDS;

/**
 * The prototype's four stages (S:13), derived from what the rows say and
 * never stored: ready to approve once it has a checked address or a
 * draft; finding people while it is only found or read and above the bar;
 * below the bar by its tier; lost or off the list when excluded.
 */
export function stageOf(p: WireProspect): string {
  if (p.stage === 'excluded') return 'Lost / off the list';
  if (p.fit && (p.fit.tier === 'weak' || p.fit.tier === 'excluded')) return 'Below the bar';
  if (p.stage === 'enriched' || p.stage === 'drafted') return 'Ready to approve';
  return 'Finding people';
}

function Discover({ markets }: { markets: { code: string; name: string }[] }) {
  const discover = useDiscover();
  const toast = useToast();
  const [query, setQuery] = useState('');
  const [market, setMarket] = useState('');
  const chosen = market || markets[0]?.code || '';
  return (
    <section className="flex flex-col gap-3" aria-label="Look for restaurants">
      <Kicker>Look for restaurants</Kicker>
      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (query.trim().length < 3 || !chosen) return;
          discover.mutate(
            { query: query.trim(), marketCode: chosen },
            { onSuccess: (r) => toast({ text: `${r.agent} is looking. Places appear here as each is read.` }) },
          );
        }}
      >
        <div className="min-w-[280px] flex-1">
          <Field label="What to look for" hint="One line, as you would type it into Google Maps. At most twenty places each time.">
            <TextInput value={query} onChange={(e) => setQuery(e.target.value)} placeholder="tasting menu restaurants in Oslo" maxLength={120} />
          </Field>
        </div>
        <Field label="Country">
          <Select
            aria-label="Country"
            
            value={chosen}
            onChange={(e) => setMarket(e.target.value)}
          >
            {markets.length === 0 && <option value="">No country is declared</option>}
            {markets.map((m) => (
              <option key={m.code} value={m.code}>
                {m.name}
              </option>
            ))}
          </Select>
        </Field>
        <Button type="submit" variant="primary" disabled={discover.isPending || query.trim().length < 3 || !chosen}>
          Look
        </Button>
      </form>
      {discover.isError && <Refusal failure={failureOf(discover.error)} compact />}
      <p className="m-0 text-dense text-secondary">
        Finding and reading a place costs nothing to write to it. An address is looked for only where the country allows a first email, and
        a draft is only ever a draft.
      </p>
    </section>
  );
}

/** What a prospect is, in facts: where it stands, its site, its register, its country, its address in words only. */
export function prospectFacts(p: WireProspect, coldContact: string): Array<[string, string | null]> {
  return [
    ['Where it is', STAGE_WORDS[p.stage]],
    ['Why it is off', p.exclusion ? `${EXCLUSION_WORDS[p.exclusion]}${p.excludedAt ? `, ${when(p.excludedAt)}` : ''}` : null],
    ['Website', p.domain],
    [
      'Register',
      p.register
        ? `${p.register.legalForm}${p.register.naturalPerson ? ', a sole trader' : ''}${p.register.registrationId ? `, number ${p.register.registrationId}` : ''}`
        : null,
    ],
    ['Country', `${countryName(p.marketCode)} — ${coldContact === 'open' ? 'open to a first email' : coldContact}`],
    ['Address', p.hasAddress ? `${p.addressKind === 'role' ? 'A shared address' : 'A person’s address'}, ${p.verification ?? 'unchecked'}. It is never shown here.` : 'None yet'],
    ['Found', when(p.createdAt)],
  ];
}

function ProspectRecord({ id, onClose }: { id: string; onClose: () => void }) {
  const q = useProspect(id);
  const navigate = useNavigate();
  const exclude = useExcludeProspect();
  const draft = useDraftProspect();
  const retry = useRetryProspect();
  const toast = useToast();
  if (q.isPending) return <Skeleton rows={4} />;
  if (q.isError) return <Refusal failure={failureOf(q.error)} compact />;
  const { prospect: p, coldContact } = q.data;
  const chip = STAGE_CHIP[p.stage];
  return (
    <section className="flex flex-col gap-4 rounded-brand border border-strong p-5" data-testid="prospect-record">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="m-0 text-panel">{p.businessName ?? p.domain ?? 'Name not written down yet'}</h2>
        <Badge tone={chip.tone}>{chip.label}</Badge>
        <Button icon="open" variant="quiet" onClick={() => void navigate(`/prospects/${p.id}`)}>Open its record</Button>
        <Button icon="close" variant="quiet" onClick={onClose}>Close</Button>
      </div>
      <FactList facts={prospectFacts(p, coldContact)} />
      {p.fit && (
        <div className="flex flex-col gap-2" data-testid="why-this-score">
          <Kicker>Why {p.fit.score}</Kicker>
          <ul className="m-0 flex list-none flex-col gap-1 p-0 text-body">
            {p.fit.reasons.map((r, i) => (
              <li key={`${r.code}-${i}`} className="flex gap-3">
                <span className="w-10 shrink-0 text-right font-mono">{r.points > 0 ? `+${r.points}` : r.points}</span>
                <span>{reasonWords(r)}</span>
              </li>
            ))}
          </ul>
          {p.googleUrl && (
            <a className="text-dense text-accent underline" href={p.googleUrl} target="_blank" rel="noreferrer noopener">
              See it on Google — what Google says is read there, live, and not kept here
            </a>
          )}
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        {p.stage === 'enriched' && (
          <Button
            variant="primary"
            disabled={draft.isPending}
            onClick={() => draft.mutate(p.id, { onSuccess: () => toast({ text: 'A first email is being written. It will wait in the shared box.' }) })}
          >
            Write the first email now
          </Button>
        )}
        {(p.stage === 'discovered' || (p.stage === 'read' && (p.foundAs ?? 'customer') === 'customer')) && (
          <Button
            disabled={retry.isPending}
            onClick={() =>
              retry.mutate(p.id, {
                onSuccess: (r) => toast({ text: r.step === 'read_site' ? 'Its website is being read again.' : 'Its address is being looked for again.' }),
              })
            }
          >
            Try again
          </Button>
        )}
        {p.stage !== 'excluded' && (
          <Button variant="danger" disabled={exclude.isPending} onClick={() => exclude.mutate(p.id)}>
            Take it off the list
          </Button>
        )}
      </div>
      {draft.isError && <Refusal failure={failureOf(draft.error)} compact />}
      {retry.isError && <Refusal failure={failureOf(retry.error)} compact />}
      {exclude.isError && <Refusal failure={failureOf(exclude.error)} compact />}
    </section>
  );
}

/** One line each: `domain, country[, name]`. Parsed here, and every line is checked again by the platform. */
export function parseImport(text: string): { domain: string; marketCode: string; businessName?: string }[] {
  return text
    .split('\n')
    .map((line) => line.split(',').map((c) => c.trim()))
    .filter((cells) => cells[0])
    .map(([domain, marketCode, ...name]) => ({
      domain: domain!,
      marketCode: (marketCode ?? '').toUpperCase(),
      ...(name.length > 0 && name.join(',').trim() ? { businessName: name.join(',').trim() } : {}),
    }));
}

function Import({ markets }: { markets: string[] }) {
  const importRows = useImportProspects();
  const [text, setText] = useState('');
  const rows = parseImport(text);
  return (
    <section className="flex flex-col gap-3" aria-label="Import a list">
      <Kicker>Import a list you already have</Kicker>
      <Field label="One restaurant a line: website, country, name" hint={`For example: maaemo.no, NO, Maaemo. Countries: ${markets.join(', ') || 'none declared'}.`}>
        <Textarea
          aria-label="List"
          className="min-h-[96px] font-mono"
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
      </Field>
      <div>
        <Button variant="quiet" disabled={rows.length === 0 || rows.length > 200 || importRows.isPending} onClick={() => importRows.mutate(rows)}>
          Import {rows.length > 0 ? rows.length : ''}
        </Button>
      </div>
      {importRows.isError && <Refusal failure={failureOf(importRows.error)} compact />}
      {importRows.data && (
        <div role="status" className="flex flex-col gap-1 text-body" data-testid="import-result">
          <p className="m-0">{importRows.data.created} added. Each is read from its own website next.</p>
          {importRows.data.problems.map((problem) => (
            <p key={problem} className="m-0 text-dense text-secondary">{problem}</p>
          ))}
        </div>
      )}
    </section>
  );
}
